import React, { useState, useEffect, useCallback } from 'react';
import { useUser } from '@clerk/clerk-react';
import * as apiUtils from './apiUtils';
import { ReactComponent as BrainIcon } from '../../assets/brain_icon.svg';
import { debugLog, debugWarn, debugError } from '../../utils/debug';

// Simple markdown renderer for basic formatting
const renderMarkdown = (text) => {
  if (!text) return '';
  
  return text
    // Bold
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    // Italic
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    // Code
    .replace(/`(.*?)`/g, '<code>$1</code>')
    // Line breaks
    .replace(/\n/g, '<br>');
};

const ContextView = ({ activeConversationId }) => {
  const { user } = useUser();
  const [contextData, setContextData] = useState(null);
  const [longTermMemory, setLongTermMemory] = useState(null);
  const [messagingSettings, setMessagingSettings] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isMemoryLoading, setIsMemoryLoading] = useState(false);
  const [isSettingsLoading, setIsSettingsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [memoryError, setMemoryError] = useState(null);
  const [settingsError, setSettingsError] = useState(null);
  const [isEditingFrequency, setIsEditingFrequency] = useState(false);
  const [tempFrequency, setTempFrequency] = useState('');
  const [expandedSections, setExpandedSections] = useState({
    metadata: true,
    longTermMemory: true,
    messagingSettings: true,
    summaries: true,
    systemPrompt: false,
    conversationHistory: false
  });
  const [expandedSummaries, setExpandedSummaries] = useState(new Set());

  const fetchContext = useCallback(async () => {
    if (!activeConversationId || !user) return;
    
    try {
      setIsLoading(true);
      setError(null);
      const data = await apiUtils.fetchConversationContext(user, activeConversationId);
      setContextData(data);
    } catch (err) {
      debugError('Error fetching context:', err);
      setError('Failed to load memory information');
    } finally {
      setIsLoading(false);
    }
  }, [activeConversationId, user]);

  const fetchLongTermMemory = useCallback(async () => {
    if (!user) return;
    
    try {
      setIsMemoryLoading(true);
      setMemoryError(null);
      const data = await apiUtils.fetchLongTermMemory(user);
      setLongTermMemory(data);
    } catch (err) {
      debugError('Error fetching long-term memory:', err);
      setMemoryError('Failed to load long-term memory');
    } finally {
      setIsMemoryLoading(false);
    }
  }, [user]);

  const refreshAndUpdateLongTermMemory = async () => {
    if (!user || !activeConversationId) {
      // If no active conversation, just refresh (fallback to original behavior)
      return fetchLongTermMemory();
    }
    
    try {
      setIsMemoryLoading(true);
      setMemoryError(null);
      
      // Step 1: Trigger LTM update for the active conversation
      debugLog(`🔄 [LTM-REFRESH] Triggering LTM update for conversation ${activeConversationId}`);
      const updateResult = await apiUtils.updateLongTermMemory(user, activeConversationId);
      
      if (updateResult.success) {
        debugLog(`✅ [LTM-REFRESH] LTM update successful`);
      } else {
        debugWarn(`⚠️ [LTM-REFRESH] LTM update returned: ${updateResult.message}`);
      }
      
      // Step 2: Wait a brief moment for the update to propagate
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Step 3: Fetch the updated LTM to display
      const data = await apiUtils.fetchLongTermMemory(user);
      setLongTermMemory(data);
      
      debugLog(`✅ [LTM-REFRESH] LTM refreshed and displayed`);
    } catch (err) {
      debugError('❌ [LTM-REFRESH] Error updating/refreshing long-term memory:', err);
      setMemoryError('Failed to update long-term memory');
      
      // Still try to fetch current LTM even if update failed
      try {
        const data = await apiUtils.fetchLongTermMemory(user);
        setLongTermMemory(data);
      } catch (fetchErr) {
        debugError('❌ [LTM-REFRESH] Failed to fetch LTM after update error:', fetchErr);
      }
    } finally {
      setIsMemoryLoading(false);
    }
  };

  const fetchMessagingSettings = useCallback(async () => {
    if (!user) return;
    
    try {
      setIsSettingsLoading(true);
      setSettingsError(null);
      const data = await apiUtils.fetchUserMessagingSettings(user);
      setMessagingSettings(data);
    } catch (err) {
      debugError('Error fetching messaging settings:', err);
      setSettingsError('Failed to load messaging settings');
    } finally {
      setIsSettingsLoading(false);
    }
  }, [user]);

  const updateMessagingFrequency = async (timeString) => {
    if (!user || !messagingSettings) return;
    
    try {
      setIsSettingsLoading(true);
      setSettingsError(null);
      
      // Parse time string to get total minutes
      const [hours, minutes, seconds] = timeString.split(':').map(Number);
      const totalMinutes = (hours * 60) + minutes + Math.floor(seconds / 60);
      
      const updatedSettings = await apiUtils.updateUserMessagingSettings(user, {
        check_interval_minutes: totalMinutes,
        enabled: messagingSettings.enabled
      });
      
      setMessagingSettings(updatedSettings);
      setIsEditingFrequency(false);
      setTempFrequency('');
    } catch (err) {
      debugError('Error updating messaging settings:', err);
      setSettingsError('Failed to update messaging settings');
    } finally {
      setIsSettingsLoading(false);
    }
  };

  const formatMinutesToTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:00`;
  };

  const validateTimeFormat = (timeString) => {
    const timeRegex = /^\d{1,2}:\d{2}:\d{2}$/;
    if (!timeRegex.test(timeString)) return false;
    
    const [hours, minutes, seconds] = timeString.split(':').map(Number);
    return hours >= 0 && minutes >= 0 && minutes < 60 && seconds >= 0 && seconds < 60;
  };

  useEffect(() => {
    fetchContext();
    fetchLongTermMemory();
    fetchMessagingSettings();
  }, [activeConversationId, user, fetchContext, fetchLongTermMemory, fetchMessagingSettings]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const toggleSummaryExpansion = (summaryId) => {
    setExpandedSummaries(prev => {
      const newSet = new Set(prev);
      if (newSet.has(summaryId)) {
        newSet.delete(summaryId);
      } else {
        newSet.add(summaryId);
      }
      return newSet;
    });
  };

  const getTruncatedSummary = (summary, summaryId, maxLength = 150) => {
    const isExpanded = expandedSummaries.has(summaryId);
    
    if (summary.length <= maxLength || isExpanded) {
      return {
        text: summary,
        showButton: summary.length > maxLength,
        isExpanded
      };
    }
    
    return {
      text: summary.substring(0, maxLength) + '...',
      showButton: true,
      isExpanded: false
    };
  };

  const formatDate = (isoString) => {
    return new Date(isoString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatConversationMessage = (message, index) => {
    if (typeof message === 'string') {
      // Legacy string format
      return message;
    } else if (typeof message === 'object' && message.role && message.content) {
      // New object format
      const roleName = message.role === 'assistant' ? 'Coach' : 'User';
      const timestamp = new Date(message.created_at).toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
      return `${roleName} (${timestamp}): "${message.content}"`;
    } else {
      // Fallback for unexpected format
      return `Message ${index + 1}: ${JSON.stringify(message)}`;
    }
  };

  if (!activeConversationId) {
    return (
      <div className="p-4 text-center text-gray-400">
        <div className="mb-2">
          <svg className="w-8 h-8 mx-auto text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
        <p className="text-sm">Select a conversation to view its AI memory</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-4 text-center">
        <div className="animate-spin w-6 h-6 border-2 border-blue-400 border-t-transparent rounded-full mx-auto mb-2"></div>
        <p className="text-sm text-gray-400">Loading memory...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-center text-red-400">
        <div className="mb-2">
          <svg className="w-8 h-8 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <p className="text-sm">{error}</p>
        <button 
          onClick={fetchContext}
          className="mt-2 text-xs text-blue-400 hover:text-blue-300"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!contextData) return null;

  const { context_metadata, summaries, system_prompt, conversation_history } = contextData;

  return (
    <div className="flex-1 overflow-y-auto" style={{
      // Mobile-specific scrolling improvements
      WebkitOverflowScrolling: 'touch',
      overscrollBehavior: 'contain',
      scrollbarWidth: 'thin',
      scrollbarColor: 'rgba(107, 114, 128, 0.6) rgba(31, 41, 55, 0.5)'
    }}>
      <div className="p-3 border-b border-gray-700">
        <div className="flex items-center gap-2 mb-2">
          <BrainIcon className="w-8 h-8 text-blue-400" />
          <h3 className="font-medium text-white">AI Memory</h3>
        </div>
        <p className="text-xs text-gray-400">
          How your coach remembers your running journey and preferences
        </p>
      </div>

      <div className="p-3 space-y-3">
        {/* Context Metadata */}
        <div>
          <button
            onClick={() => toggleSection('metadata')}
            className="flex items-center justify-between w-full text-left"
          >
            <span className="text-sm font-medium text-gray-200">Memory Overview</span>
            <svg 
              className={`w-4 h-4 text-gray-400 transition-transform ${expandedSections.metadata ? 'rotate-180' : ''}`}
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          
          {expandedSections.metadata && (
            <div className="mt-2 p-3 bg-gray-800/50 rounded-lg text-xs">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <span className="text-gray-400">Total Summaries:</span>
                  <div className="text-blue-400 font-medium">{context_metadata.summaries_count}</div>
                </div>
                <div>
                  <span className="text-gray-400">Current Messages:</span>
                  <div className="text-blue-400 font-medium">{context_metadata.total_messages}</div>
                </div>
                <div>
                  <span className="text-gray-400">Recent Summaries:</span>
                  <div className="text-green-400 font-medium">{context_metadata.recent_summaries_count}</div>
                </div>
                <div>
                  <span className="text-gray-400">Has Long-term Memory:</span>
                  <div className={`font-medium ${context_metadata.has_long_term_memory ? 'text-green-400' : 'text-gray-500'}`}>
                    {context_metadata.has_long_term_memory ? 'Yes' : 'No'}
                  </div>
                </div>
              </div>
              <div className="mt-2 pt-2 border-t border-gray-700">
                <span className="text-gray-400">Generated:</span>
                <div className="text-gray-300">{formatDate(context_metadata.generated_at)}</div>
              </div>
            </div>
          )}
        </div>

        {/* Long-term Memory */}
        <div>
          <div className="flex items-center justify-between">
            <button
              onClick={() => toggleSection('longTermMemory')}
              className="flex items-center gap-2 text-left flex-1"
            >
              <span className="text-sm font-medium text-gray-200 flex items-center gap-2">
                <svg className="w-4 h-4 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
                Long-term Memory
              </span>
              <svg 
                className={`w-4 h-4 text-gray-400 transition-transform ${expandedSections.longTermMemory ? 'rotate-180' : ''}`}
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <button
              onClick={refreshAndUpdateLongTermMemory}
              disabled={isMemoryLoading}
              className="ml-2 p-1 text-gray-400 hover:text-gray-200 disabled:opacity-50"
              title="Update and refresh long-term memory"
            >
              <svg className={`w-4 h-4 ${isMemoryLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          </div>
          
          {expandedSections.longTermMemory && (
            <div className="mt-2">
              {isMemoryLoading ? (
                <div className="p-3 bg-gray-800/50 rounded-lg text-center">
                  <div className="animate-spin w-4 h-4 border-2 border-purple-400 border-t-transparent rounded-full mx-auto mb-2"></div>
                  <p className="text-xs text-gray-400">Loading memory...</p>
                </div>
              ) : memoryError ? (
                <div className="p-3 bg-red-900/20 border border-red-800 rounded-lg">
                  <p className="text-xs text-red-400">{memoryError}</p>
                  <button 
                    onClick={fetchLongTermMemory}
                    className="mt-2 text-xs text-red-300 hover:text-red-200"
                  >
                    Retry
                  </button>
                </div>
              ) : longTermMemory && longTermMemory.content ? (
                <div className="p-3 bg-purple-900/20 border border-purple-800 rounded-lg">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-xs text-purple-300 font-medium">Coach's Memory Bank</span>
                    <span className="text-xs text-gray-400">
                      Updated: {formatDate(longTermMemory.updated_at)}
                    </span>
                  </div>
                  <div 
                    className="text-xs text-gray-300 whitespace-pre-wrap leading-relaxed max-h-64 overflow-y-auto"
                    dangerouslySetInnerHTML={{ __html: renderMarkdown(longTermMemory.content) }}
                  />
                </div>
              ) : (
                <div className="p-3 bg-gray-800/50 rounded-lg text-xs text-gray-400">
                  No long-term memory yet. The AI will develop its inner world as you interact more.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Messaging Settings */}
        <div>
          <div className="flex items-center justify-between">
            <button
              onClick={() => toggleSection('messagingSettings')}
              className="flex items-center gap-2 text-left flex-1"
            >
              <span className="text-sm font-medium text-gray-200 flex items-center gap-2">
                <svg className="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Messaging Settings
              </span>
              <svg 
                className={`w-4 h-4 text-gray-400 transition-transform ${expandedSections.messagingSettings ? 'rotate-180' : ''}`}
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <button
              onClick={fetchMessagingSettings}
              disabled={isSettingsLoading}
              className="ml-2 p-1 text-gray-400 hover:text-gray-200 disabled:opacity-50"
              title="Refresh messaging settings"
            >
              <svg className={`w-4 h-4 ${isSettingsLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          </div>
          
          {expandedSections.messagingSettings && (
            <div className="mt-2">
              {isSettingsLoading ? (
                <div className="p-3 bg-gray-800/50 rounded-lg text-center">
                  <div className="animate-spin w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full mx-auto mb-2"></div>
                  <p className="text-xs text-gray-400">Loading settings...</p>
                </div>
              ) : settingsError ? (
                <div className="p-3 bg-red-900/20 border border-red-800 rounded-lg">
                  <p className="text-xs text-red-400">{settingsError}</p>
                  <button 
                    onClick={fetchMessagingSettings}
                    className="mt-2 text-xs text-red-300 hover:text-red-200"
                  >
                    Retry
                  </button>
                </div>
              ) : messagingSettings ? (
                <div className="p-3 bg-blue-900/20 border border-blue-800 rounded-lg">
                  <div className="mb-3 flex items-center justify-between">
                    <span className="text-xs text-blue-300 font-medium">Unprompted Message Frequency</span>
                    <span className="text-xs text-gray-400">
                      Last checked: {messagingSettings.last_checked_at ? formatDate(messagingSettings.last_checked_at) : 'Never'}
                    </span>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-300">Status:</span>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        messagingSettings.enabled 
                          ? 'bg-green-500/20 text-green-400' 
                          : 'bg-gray-500/20 text-gray-400'
                      }`}>
                        {messagingSettings.enabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-300">Check Frequency:</span>
                        {!isEditingFrequency ? (
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-blue-400 font-mono">
                              {formatMinutesToTime(messagingSettings.check_interval_minutes)}
                            </span>
                            <button
                              onClick={() => {
                                setIsEditingFrequency(true);
                                setTempFrequency(formatMinutesToTime(messagingSettings.check_interval_minutes));
                              }}
                              className="text-xs text-gray-400 hover:text-gray-200"
                              title="Edit frequency"
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                              </svg>
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1">
                            <input
                              type="text"
                              value={tempFrequency}
                              onChange={(e) => setTempFrequency(e.target.value)}
                              className="w-20 px-1 py-1 text-xs bg-gray-700 border border-gray-600 rounded text-white font-mono"
                              placeholder="HH:MM:SS"
                              autoFocus
                            />
                            <button
                              onClick={() => {
                                if (validateTimeFormat(tempFrequency)) {
                                  updateMessagingFrequency(tempFrequency);
                                } else {
                                  setSettingsError('Invalid time format. Use HH:MM:SS');
                                }
                              }}
                              disabled={!tempFrequency || isSettingsLoading}
                              className="p-1 text-green-400 hover:text-green-300 disabled:opacity-50"
                              title="Save"
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                              </svg>
                            </button>
                            <button
                              onClick={() => {
                                setIsEditingFrequency(false);
                                setTempFrequency('');
                                setSettingsError(null);
                              }}
                              className="p-1 text-gray-400 hover:text-gray-300"
                              title="Cancel"
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        )}
                      </div>
                      
                      <div className="text-xs text-gray-400">
                        The AI can send unprompted messages every {Math.floor(messagingSettings.check_interval_minutes / 60)}h {messagingSettings.check_interval_minutes % 60}m
                        {messagingSettings.check_interval_minutes < 60 && ' (minimum 10 minutes)'}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-3 bg-gray-800/50 rounded-lg text-xs text-gray-400">
                  No messaging settings available.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Conversation Summaries */}
        <div>
          <button
            onClick={() => toggleSection('summaries')}
            className="flex items-center justify-between w-full text-left"
          >
            <span className="text-sm font-medium text-gray-200">
              Previous Conversations ({summaries.length})
            </span>
            <svg 
              className={`w-4 h-4 text-gray-400 transition-transform ${expandedSections.summaries ? 'rotate-180' : ''}`}
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          
          {expandedSections.summaries && (
            <div className="mt-2 space-y-2">
              {summaries.length === 0 ? (
                <div className="p-3 bg-gray-800/50 rounded-lg text-xs text-gray-400">
                  No previous conversation summaries available
                </div>
              ) : (
                summaries.map((summary, index) => (
                  <div key={`summary-${summary.id}-${index}`} className="p-3 bg-gray-800/50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-gray-300">
                        Conversation #{summary.conversation_id}
                      </span>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          summary.type === 'recent' 
                            ? 'bg-blue-500/20 text-blue-400' 
                            : 'bg-purple-500/20 text-purple-400'
                        }`}>
                          {summary.type === 'recent' ? 'Recent' : 'Last Updated'}
                        </span>
                        <span className="text-xs text-gray-500">
                          {formatDate(summary.conversation_created_at)}
                        </span>
                      </div>
                    </div>
                    <div 
                      className="text-xs text-gray-300 leading-relaxed"
                      dangerouslySetInnerHTML={{ 
                        __html: renderMarkdown(getTruncatedSummary(summary.summary, summary.id).text) 
                      }}
                    />
                    {getTruncatedSummary(summary.summary, summary.id).showButton && (
                      <button
                        onClick={() => toggleSummaryExpansion(summary.id)}
                        className="mt-1 text-xs text-blue-400 hover:text-blue-300"
                      >
                        {getTruncatedSummary(summary.summary, summary.id).isExpanded ? 'Show Less' : 'Show More'}
                      </button>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Current Conversation Format */}
        <div>
          <button
            onClick={() => toggleSection('conversationHistory')}
            className="flex items-center justify-between w-full text-left"
          >
            <span className="text-sm font-medium text-gray-200">
              Current Conversation Format ({conversation_history.length > 5 ? '5 of ' + conversation_history.length : conversation_history.length})
            </span>
            <svg 
              className={`w-4 h-4 text-gray-400 transition-transform ${expandedSections.conversationHistory ? 'rotate-180' : ''}`}
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          
          {expandedSections.conversationHistory && (
            <div className="mt-2 p-3 bg-gray-800/50 rounded-lg">
              {conversation_history.slice(0, 5).map((message, index) => (
                <div key={`conversation-${index}`} className="text-xs text-gray-300 mb-2 font-mono leading-relaxed">
                  {formatConversationMessage(message, index)}
                </div>
              ))}
              {conversation_history.length > 5 && (
                <div className="text-xs text-gray-400 mt-3 pt-2 border-t border-gray-700">
                  Showing first 5 of {conversation_history.length} messages
                </div>
              )}
            </div>
          )}
        </div>

        {/* System Prompt */}
        <div>
          <button
            onClick={() => toggleSection('systemPrompt')}
            className="flex items-center justify-between w-full text-left"
          >
            <span className="text-sm font-medium text-gray-200">System Prompt</span>
            <svg 
              className={`w-4 h-4 text-gray-400 transition-transform ${expandedSections.systemPrompt ? 'rotate-180' : ''}`}
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          
          {expandedSections.systemPrompt && (
            <div className="mt-2 p-3 bg-gray-800/50 rounded-lg">
              <pre className="text-xs text-gray-300 whitespace-pre-wrap font-mono leading-relaxed">
                {system_prompt}
              </pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContextView; 